import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Chip, LinearProgress,
    List, ListItem, ListItemText, Avatar, Divider, Skeleton,
    useTheme, useMediaQuery
} from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
    format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
    eachDayOfInterval, isSameDay, min as dfMin, max as dfMax, differenceInDays
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../services/api';
import PainelDoDia from './PainelDoDia';

import KpiCard from './dashboard/KpiCard';
import EmptyState from './dashboard/EmptyState';
import SkeletonCard from './dashboard/SkeletonCard';
import MiniBarChart from './dashboard/MiniBarChart';

const PRIMARY = '#1ABB9C';

const fmtMoeda = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (d) => {
    if (!d) return '-';
    try { return format(new Date(d + 'T00:00:00'), 'dd/MM/yyyy'); } catch { return d; }
};
const hojeStr = () => format(new Date(), 'yyyy-MM-dd');
const isoDateTime = (d) => format(d, "yyyy-MM-dd'T'HH:mm:ss");

// Anonimiza nome: primeiro nome + inicial do sobrenome.
const anonNome = (nome) => {
    if (!nome) return 'Paciente';
    const parts = nome.trim().split(/\s+/);
    return parts.length === 1 ? parts[0] : `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const SectionTitle = ({ children }) => (
    <Typography variant="h6" component="h2" sx={{ color: PRIMARY, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box aria-hidden="true" sx={{ width: 4, height: 22, bgcolor: PRIMARY, borderRadius: 1, flexShrink: 0 }} />
        {children}
    </Typography>
);

const ListSkeleton = ({ rows = 4 }) => (
    <Box>
        {Array.from({ length: rows }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                </Box>
            </Box>
        ))}
    </Box>
);

const StatusChip = ({ status }) => {
    const map = {
        PENDENTE: { label: 'Pendente', color: 'warning' },
        VENCIDO: { label: 'Vencido', color: 'error' },
        PAGO: { label: 'Pago', color: 'success' },
        PARCIAL: { label: 'Parcial', color: 'info' },
        CANCELADO: { label: 'Cancelado', color: 'default' },
    };
    const { label = status, color = 'default' } = map[status] || {};
    return <Chip label={label} color={color} size="small" sx={{ fontWeight: 600 }} />;
};

const WEEK_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

export default function Dashboard() {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(true);
    const [pacientes, setPacientes] = useState([]);
    const [slotsHoje, setSlotsHoje] = useState([]);
    // Indicadores financeiros vêm AGREGADOS do backend (mesmo critério do painel
    // Financeiro e da ficha do paciente): não se carrega mais a lista inteira de
    // títulos para somar no navegador.
    const [resumoAberto, setResumoAberto] = useState(null);      // saldo em aberto, todos os vencimentos
    const [resumoMesReceber, setResumoMesReceber] = useState(null);
    const [resumoMesPagar, setResumoMesPagar] = useState(null);
    const [saldoBancario, setSaldoBancario] = useState(null);
    const [contasReceberAlerta, setContasReceberAlerta] = useState([]);
    // Falha de carregamento NÃO pode virar "R$ 0,00 válido".
    const [erroFinanceiro, setErroFinanceiro] = useState(false);
    const [filaEspera, setFilaEspera] = useState([]);
    const [renovacoesPendentes, setRenovacoesPendentes] = useState([]);
    const [sessoesPeriodo, setSessoesPeriodo] = useState([]);

    useEffect(() => {
        const todayStr = hojeStr();
        const now = new Date();
        const wkStart = startOfWeek(now, { weekStartsOn: 1 });
        const wkEnd = endOfWeek(now, { weekStartsOn: 1 });
        const inicio = isoDateTime(dfMin([wkStart, startOfMonth(now)]));
        const fim = isoDateTime(dfMax([wkEnd, endOfMonth(now)]));

        const mesAtual = format(now, 'yyyy-MM');
        const mesInicio = format(startOfMonth(now), 'yyyy-MM-dd');
        const mesFim = format(endOfMonth(now), 'yyyy-MM-dd');

        const cfg = { skipGlobalError: true };
        Promise.allSettled([
            api.get('/pacientes', cfg),
            api.get('/agenda/slots', { params: { inicio: todayStr, fim: todayStr }, ...cfg }),
            // Saldo em aberto de TODOS os vencimentos (inclui PARCIAL, exclui cancelados).
            api.get('/contas-receber/resumo', cfg),
            // Previsto do mês por vencimento + realizado do mês por movimentação.
            api.get('/contas-receber/resumo', { params: { mes: mesAtual, dataPagamentoInicio: mesInicio, dataPagamentoFim: mesFim }, ...cfg }),
            api.get('/contas-pagar/resumo', { params: { mes: mesAtual, dataPagamentoInicio: mesInicio, dataPagamentoFim: mesFim }, ...cfg }),
            api.get('/contas-bancarias', cfg),
            api.get('/fila-espera', cfg),
            api.get('/renovacao-assinatura/pendentes', cfg),
            api.get('/sessoes/periodo', { params: { inicio, fim }, ...cfg }),
            // Alerta: vencidos e a vencer em 30 dias, por vencimento.
            api.get('/contas-receber/paginado', { params: { vencidas: true, size: 6, sort: 'dataVencimento,asc' }, ...cfg }),
            api.get('/contas-receber/paginado', { params: { vencendoEm: 30, size: 6, sort: 'dataVencimento,asc' }, ...cfg }),
        ]).then(([rPac, rSlots, rAberto, rMesReceber, rMesPagar, rBancos, rFila, rRen, rSess, rVencidas, rAVencer]) => {
            const toArray = (data) => Array.isArray(data) ? data : (data?.content ?? []);
            if (rPac.status === 'fulfilled') setPacientes(toArray(rPac.value.data));
            if (rSlots.status === 'fulfilled') setSlotsHoje(toArray(rSlots.value.data));
            if (rAberto.status === 'fulfilled') setResumoAberto(rAberto.value.data);
            if (rMesReceber.status === 'fulfilled') setResumoMesReceber(rMesReceber.value.data);
            if (rMesPagar.status === 'fulfilled') setResumoMesPagar(rMesPagar.value.data);
            if (rBancos.status === 'fulfilled') {
                setSaldoBancario(toArray(rBancos.value.data)
                    .reduce((acc, c) => acc + Number(c.saldoAtual || 0), 0));
            }
            if (rFila.status === 'fulfilled') setFilaEspera(toArray(rFila.value.data));
            if (rRen.status === 'fulfilled') setRenovacoesPendentes(toArray(rRen.value.data));
            if (rSess.status === 'fulfilled') setSessoesPeriodo(toArray(rSess.value.data));

            const alerta = [];
            if (rVencidas.status === 'fulfilled') alerta.push(...toArray(rVencidas.value.data));
            if (rAVencer.status === 'fulfilled') alerta.push(...toArray(rAVencer.value.data));
            const vistos = new Set();
            setContasReceberAlerta(alerta
                .filter(c => { const k = c.id_c_receber || c.id; if (vistos.has(k)) return false; vistos.add(k); return true; })
                .sort((a, b) => new Date(a.dataVencimento) - new Date(b.dataVencimento))
                .slice(0, 6));

            setErroFinanceiro([rAberto, rMesReceber, rMesPagar, rBancos].some(r => r.status !== 'fulfilled'));
            setLoading(false);
        });
    }, []);

    // ── KPIs linha 1 ──────────────────────────────────────────────
    const totalPacientes = pacientes.length;
    const pacientesAtivos = pacientes.filter(p => p.statusTratamento === 'ATIVO').length;
    const sessoesHoje = slotsHoje.filter(s => !s.disponivel).length;

    // Pendência = SALDO EM ABERTO (valor - valorPago) de títulos ativos: inclui os
    // PARCIAIS e exclui os cancelados. Antes somava o valor integral de
    // PENDENTE/VENCIDO — título de R$ 300 com R$ 100 recebido aparecia como R$ 300,
    // e título PARCIAL não entrava na conta.
    const aReceberEmAberto = resumoAberto ? Number(resumoAberto.totalEmAberto || 0) : null;
    const aReceberVencido = resumoAberto ? Number(resumoAberto.quantidadeVencido || 0) : 0;
    const aReceberValorVencido = resumoAberto ? Number(resumoAberto.totalVencido || 0) : 0;

    const filaAtiva = filaEspera.length;
    const filaMais30 = filaEspera.filter(f => f.dataEntrada && differenceInDays(new Date(), new Date(f.dataEntrada)) > 30).length;

    // ── KPIs linha 2 (derivados de sessões) ───────────────────────
    const now = new Date();
    const weekDays = eachDayOfInterval({ start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) });
    const sessoesValidas = sessoesPeriodo.filter(s => s.statusSessao !== 'CANCELADA' && s.dataHora);
    const barData = weekDays.map((day, i) => ({
        label: WEEK_LABELS[i],
        value: sessoesValidas.filter(s => isSameDay(new Date(s.dataHora), day)).length,
        highlight: isSameDay(day, now),
    }));
    const sessoesSemana = barData.reduce((a, b) => a + b.value, 0);

    const mesSessoes = sessoesPeriodo.filter(s => {
        if (!s.dataHora) return false;
        const d = new Date(s.dataHora);
        return d >= startOfMonth(now) && d <= endOfMonth(now);
    });
    const realizadas = mesSessoes.filter(s => s.statusSessao === 'REALIZADA' || s.statusSessao === 'REALIZADA_ALTERADA').length;
    const faltas = mesSessoes.filter(s => s.statusSessao === 'FALTA').length;
    const baseComparecimento = realizadas + faltas;
    const taxaComparecimento = baseComparecimento > 0 ? Math.round((realizadas / baseComparecimento) * 100) : null;

    const proximaSessao = sessoesPeriodo
        .filter(s => s.statusSessao === 'AGENDADA' && s.dataHora && new Date(s.dataHora) > now && isSameDay(new Date(s.dataHora), now))
        .sort((a, b) => new Date(a.dataHora) - new Date(b.dataHora))[0];

    // Sessões já ocorridas e ainda sem evolução registrada
    const sessoesAEvoluir = sessoesPeriodo
        .filter(s => s.dataHora && new Date(s.dataHora) < now && s.statusSessao !== 'CANCELADA' && !s.temEvolucao).length;

    // Fluxo do mês com os três conceitos separados:
    // - PREVISTO: títulos do mês por VENCIMENTO, cancelados fora (totalGeral);
    // - REALIZADO: movimentações do mês por data de pagamento, líquido de estornos;
    // - SALDO BANCÁRIO: soma do saldo das contas — estoque, não fluxo.
    const previstoReceitas = resumoMesReceber ? Number(resumoMesReceber.totalGeral || 0) : null;
    const previstoDespesas = resumoMesPagar ? Number(resumoMesPagar.totalGeral || 0) : null;
    const realizadoReceitas = resumoMesReceber ? Number(resumoMesReceber.recebidoNoPeriodo || 0) : null;
    const realizadoDespesas = resumoMesPagar ? Number(resumoMesPagar.pagoNoPeriodo || 0) : null;
    const saldoPrevistoMes = (previstoReceitas != null && previstoDespesas != null)
        ? previstoReceitas - previstoDespesas : null;
    const saldoRealizadoMes = (realizadoReceitas != null && realizadoDespesas != null)
        ? realizadoReceitas - realizadoDespesas : null;

    // Ocupação da agenda de hoje
    const totalSlots = slotsHoje.length;
    const ocupacao = totalSlots > 0 ? Math.round((slotsHoje.filter(s => !s.disponivel).length / totalSlots) * 100) : 0;

    // ── Listas ────────────────────────────────────────────────────
    const filaTop = [...filaEspera].sort((a, b) => new Date(a.dataEntrada) - new Date(b.dataEntrada)).slice(0, 6);
    const slotsOcupados = slotsHoje.filter(s => !s.disponivel).sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
    const slotsLivres = slotsHoje.filter(s => s.disponivel).length;

    const dataHojeRaw = format(now, "EEEE, d 'de' MMMM", { locale: ptBR });
    const dataHoje = dataHojeRaw.charAt(0).toUpperCase() + dataHojeRaw.slice(1);

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* HEADER */}
            <Box component="header" sx={{ mb: 3 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" fontWeight="bold" color="#333">
                    Hoje
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {dataHoje}
                </Typography>
            </Box>

            {/* Painel do dia: o que precisa de acao agora, com indicadores que abrem
                a lista correspondente (achado L2). Carrega os proprios dados e
                trata falha por conta propria. */}
            <PainelDoDia />

            {/* ROW 1: KPI CARDS */}
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch" sx={{ mb: 3 }} component="section" aria-label="Indicadores principais">
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}><SkeletonCard /></Grid>
                    ))
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="Pacientes"
                                value={totalPacientes}
                                subtitle={`${pacientesAtivos} em atendimento ativo`}
                                icon={<PeopleAltOutlinedIcon />}
                                onClick={() => navigate('/pacientes')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="Sessões Hoje"
                                value={sessoesHoje}
                                subtitle={sessoesHoje > 0 ? `${sessoesHoje} confirmada${sessoesHoje !== 1 ? 's' : ''}` : 'Nenhuma agendada'}
                                icon={<EventOutlinedIcon />}
                                onClick={() => navigate('/agendamentos/calendario')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="A Receber em aberto"
                                value={aReceberEmAberto == null ? '—' : fmtMoeda(aReceberEmAberto)}
                                valueColor={aReceberEmAberto == null ? '#9ca3af' : (aReceberVencido > 0 ? '#dc2626' : PRIMARY)}
                                accent={aReceberVencido > 0 ? '#dc2626' : PRIMARY}
                                badge={aReceberVencido > 0 ? { label: `${aReceberVencido} vencido${aReceberVencido !== 1 ? 's' : ''} · ${fmtMoeda(aReceberValorVencido)}`, color: 'error' } : undefined}
                                subtitle={aReceberEmAberto == null
                                    ? 'Não foi possível carregar'
                                    : 'Saldo em aberto · todos os vencimentos'}
                                icon={<AccountBalanceWalletOutlinedIcon />}
                                onClick={() => navigate('/contas-receber')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard
                                title="Fila de Espera"
                                value={filaAtiva}
                                subtitle={filaMais30 > 0 ? `${filaMais30} há mais de 30 dias` : 'Nenhum há mais de 30 dias'}
                                icon={<HourglassEmptyOutlinedIcon />}
                                onClick={() => navigate('/agendamentos/fila-espera')}
                            />
                        </Grid>
                    </>
                )}
            </Grid>

            {/* ROW 2: NOVOS KPIs */}
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch" sx={{ mb: 1 }} component="section" aria-label="Métricas de sessões">
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}><SkeletonCard /></Grid>
                    ))
                ) : (
                    <>
                        {/* Sessões da Semana + mini gráfico */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Paper component="article" elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Sessões da Semana</Typography>
                                        <Typography role="status" fontWeight={700} sx={{ color: '#1f2937', fontSize: { xs: '1.4rem', sm: '1.7rem' }, lineHeight: 1.1 }}>
                                            {sessoesSemana}
                                        </Typography>
                                    </Box>
                                    <Avatar aria-hidden="true" sx={{ bgcolor: PRIMARY + '18', color: PRIMARY, width: 46, height: 46 }}>
                                        <CalendarMonthOutlinedIcon />
                                    </Avatar>
                                </Box>
                                <MiniBarChart data={barData} />
                            </Paper>
                        </Grid>

                        {/* Taxa de Comparecimento */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <KpiCard
                                title="Taxa de Comparecimento"
                                value={taxaComparecimento === null ? '—' : `${taxaComparecimento}%`}
                                subtitle={taxaComparecimento === null ? 'Sem sessões fechadas no mês' : `${realizadas} realizada${realizadas !== 1 ? 's' : ''} · ${faltas} falta${faltas !== 1 ? 's' : ''}`}
                                icon={<InsightsOutlinedIcon />}
                                onClick={() => navigate('/sessoes')}
                            />
                        </Grid>

                        {/* Próxima Sessão */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Paper component="article" elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Próxima Sessão</Typography>
                                        {proximaSessao ? (
                                            <>
                                                <Typography role="status" fontWeight={700} sx={{ color: PRIMARY, fontSize: { xs: '1.4rem', sm: '1.7rem' }, lineHeight: 1.1 }}>
                                                    {format(new Date(proximaSessao.dataHora), 'HH:mm')}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                    {anonNome(proximaSessao.nomePaciente)}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <Typography fontWeight={700} sx={{ color: '#9ca3af', fontSize: { xs: '1.2rem', sm: '1.4rem' }, lineHeight: 1.1, my: 0.5 }}>
                                                    —
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">Nada mais para hoje</Typography>
                                            </>
                                        )}
                                    </Box>
                                    <Avatar aria-hidden="true" sx={{ bgcolor: PRIMARY + '18', color: PRIMARY, width: 46, height: 46, ml: 1 }}>
                                        <ScheduleOutlinedIcon />
                                    </Avatar>
                                </Box>
                            </Paper>
                        </Grid>
                    </>
                )}
            </Grid>

            {/* ROW 2b: A EVOLUIR + FLUXO DO MÊS + OCUPAÇÃO */}
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch" sx={{ mb: 3 }} component="section" aria-label="Indicadores operacionais">
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}><SkeletonCard /></Grid>
                    ))
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <KpiCard
                                title="Sessões a Evoluir"
                                value={sessoesAEvoluir}
                                subtitle={sessoesAEvoluir > 0 ? 'pendentes de registro' : 'tudo registrado'}
                                accent={sessoesAEvoluir > 0 ? '#dc2626' : PRIMARY}
                                valueColor={sessoesAEvoluir > 0 ? '#dc2626' : '#1f2937'}
                                icon={<EditNoteOutlinedIcon />}
                                onClick={() => navigate('/sessoes')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Paper component="article" elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                            Fluxo do mês ({format(now, 'MM/yyyy')})
                                        </Typography>
                                        <Typography role="status" fontWeight={700} sx={{ color: saldoRealizadoMes == null ? '#9ca3af' : (saldoRealizadoMes >= 0 ? PRIMARY : '#dc2626'), fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1.1, my: 0.5 }}>
                                            {saldoRealizadoMes == null ? '—' : fmtMoeda(saldoRealizadoMes)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Realizado (caixa do mês)
                                        </Typography>
                                    </Box>
                                    <Avatar aria-hidden="true" sx={{ bgcolor: PRIMARY + '18', color: PRIMARY, width: 46, height: 46, ml: 1 }}>
                                        <SavingsOutlinedIcon />
                                    </Avatar>
                                </Box>
                                {/* Previsto (vencimento), realizado (movimentação) e saldo bancário são
                                    grandezas diferentes: ficam rotulados, nunca somados entre si. */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.25 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Recebido no mês: {realizadoReceitas == null ? '—' : fmtMoeda(realizadoReceitas)} · Pago: {realizadoDespesas == null ? '—' : fmtMoeda(realizadoDespesas)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Previsto por vencimento: {saldoPrevistoMes == null ? '—' : fmtMoeda(saldoPrevistoMes)} ({previstoReceitas == null ? '—' : fmtMoeda(previstoReceitas)} − {previstoDespesas == null ? '—' : fmtMoeda(previstoDespesas)})
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Saldo bancário atual: {saldoBancario == null ? '—' : fmtMoeda(saldoBancario)}
                                    </Typography>
                                    {erroFinanceiro && (
                                        <Typography variant="caption" sx={{ color: '#dc2626' }}>
                                            Parte dos indicadores não pôde ser carregada — os valores exibidos podem estar incompletos.
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Paper component="article" elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Ocupação da Agenda</Typography>
                                        <Typography role="status" fontWeight={700} sx={{ color: '#1f2937', fontSize: { xs: '1.4rem', sm: '1.7rem' }, lineHeight: 1.1, my: 0.5 }}>
                                            {ocupacao}%
                                        </Typography>
                                    </Box>
                                    <Avatar aria-hidden="true" sx={{ bgcolor: PRIMARY + '18', color: PRIMARY, width: 46, height: 46, ml: 1 }}>
                                        <DonutLargeOutlinedIcon />
                                    </Avatar>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={ocupacao}
                                    aria-label={`Ocupação da agenda: ${ocupacao}%`}
                                    sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: '#eef2f1', '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {totalSlots > 0 ? `${sessoesHoje} de ${totalSlots} horários hoje` : 'Agenda não gerada'}
                                </Typography>
                            </Paper>
                        </Grid>
                    </>
                )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* ROW 3: AGENDA HOJE + RENOVAÇÕES */}
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch" sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper component="section" aria-label="Agenda de hoje" elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', height: '100%' }}>
                        <SectionTitle>Agenda de Hoje</SectionTitle>
                        {loading ? (
                            <ListSkeleton rows={3} />
                        ) : slotsOcupados.length === 0 ? (
                            <EmptyState
                                icon={<EventBusyOutlinedIcon />}
                                title={slotsHoje.length === 0 ? 'Agenda não gerada' : 'Nenhuma sessão hoje'}
                                description={slotsHoje.length === 0 ? 'Gere a agenda para liberar horários de atendimento.' : 'Você não tem sessões agendadas para hoje.'}
                                actionLabel={slotsHoje.length === 0 ? 'Gerar Agenda' : 'Ver Agendamentos'}
                                onAction={() => navigate(slotsHoje.length === 0 ? '/agendamentos/disponibilidade' : '/agendamentos/calendario')}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {slotsOcupados.map(slot => (
                                    <Box key={slot.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f0fdf9', border: '1px solid #d1fae5' }}>
                                        <Box sx={{ textAlign: 'center', minWidth: 56 }}>
                                            <Typography variant="body2" fontWeight={700} color={PRIMARY}>{slot.horaInicio?.substring(0, 5)}</Typography>
                                            <Typography variant="caption" color="text.secondary">{slot.horaFim?.substring(0, 5)}</Typography>
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>Sessão agendada</Typography>
                                            <Typography variant="caption" color="text.secondary">Horário ocupado</Typography>
                                        </Box>
                                        <FiberManualRecordIcon aria-hidden="true" sx={{ color: PRIMARY, fontSize: 10 }} />
                                    </Box>
                                ))}
                                {slotsLivres > 0 && (
                                    <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#fafafa', border: '1px dashed #e0e0e0', textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            + {slotsLivres} horário{slotsLivres !== 1 ? 's' : ''} disponível{slotsLivres !== 1 ? 'is' : ''}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper component="section" aria-label="Renovações pendentes" elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', height: '100%' }}>
                        <SectionTitle>Renovações Pendentes</SectionTitle>
                        {loading ? (
                            <ListSkeleton rows={3} />
                        ) : renovacoesPendentes.length === 0 ? (
                            <EmptyState
                                icon={<AssignmentOutlinedIcon />}
                                title="Nenhuma renovação pendente"
                                description="Assinaturas a renovar aparecerão aqui."
                                actionLabel="Ver Assinaturas"
                                onAction={() => navigate('/assinaturas-paciente')}
                            />
                        ) : (
                            <List disablePadding>
                                {renovacoesPendentes.slice(0, 6).map((ren, idx, arr) => (
                                    <React.Fragment key={ren.id || idx}>
                                        <ListItem disablePadding sx={{ py: 1 }}>
                                            <Avatar sx={{ bgcolor: '#fff7ed', color: '#f59e0b', mr: 1.5, width: 36, height: 36, flexShrink: 0 }}>
                                                <AssignmentOutlinedIcon fontSize="small" />
                                            </Avatar>
                                            <ListItemText sx={{ overflow: 'hidden', minWidth: 0 }}
                                                primary={<Typography variant="body2" fontWeight={600} noWrap>{ren.nomePaciente || 'Paciente'}</Typography>}
                                                secondary={<Typography variant="caption" color="text.secondary" noWrap>{ren.nomePlano || ren.descricao || 'Renovação de assinatura'}</Typography>}
                                            />
                                            <Chip label="Pendente" color="warning" size="small" sx={{ fontWeight: 600, flexShrink: 0, ml: 1 }} />
                                        </ListItem>
                                        {idx < arr.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* ROW 4: CONTAS A RECEBER + FILA DE ESPERA */}
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch">
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper component="section" aria-label="Contas a receber" elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <SectionTitle>Contas a Receber</SectionTitle>
                        {loading ? (
                            <ListSkeleton rows={4} />
                        ) : contasReceberAlerta.length === 0 ? (
                            <EmptyState
                                icon={<CheckCircleOutlineIcon />}
                                title="Nenhuma conta pendente"
                                description="Tudo em dia — não há cobranças em aberto."
                            />
                        ) : (
                            <List disablePadding>
                                {contasReceberAlerta.map((c, idx, arr) => (
                                    <React.Fragment key={c.id_c_receber || c.id}>
                                        <ListItem disablePadding sx={{ py: 1.2 }}>
                                            <Avatar sx={{
                                                bgcolor: c.statusFinanceiro === 'VENCIDO' ? '#fef2f2' : '#fffbeb',
                                                color: c.statusFinanceiro === 'VENCIDO' ? '#ef4444' : '#f59e0b',
                                                mr: 1.5, width: 36, height: 36, flexShrink: 0
                                            }}>
                                                <WarningAmberIcon fontSize="small" />
                                            </Avatar>
                                            <ListItemText sx={{ overflow: 'hidden', minWidth: 0 }}
                                                primary={<Typography variant="body2" fontWeight={600} noWrap>{c.descricao || c.descricaoAssinatura || 'Cobrança'}</Typography>}
                                                secondary={<Typography variant="caption" color="text.secondary" noWrap>Vence: {fmtData(c.dataVencimento)}</Typography>}
                                            />
                                            <Box sx={{ textAlign: 'right', ml: 1, flexShrink: 0 }}>
                                                {/* Mostra o SALDO em aberto: em título parcial o valor
                                                    integral daria a impressão de que nada foi recebido. */}
                                                <Typography variant="body2" fontWeight={700}>
                                                    {fmtMoeda(c.saldoEmAberto != null ? c.saldoEmAberto : Number(c.valor || 0) - Number(c.valorPago || 0))}
                                                </Typography>
                                                <StatusChip status={c.statusFinanceiro} />
                                            </Box>
                                        </ListItem>
                                        {idx < arr.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper component="section" aria-label="Fila de espera" elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <SectionTitle>Fila de Espera</SectionTitle>
                        {loading ? (
                            <ListSkeleton rows={4} />
                        ) : filaTop.length === 0 ? (
                            <EmptyState
                                icon={<PersonOffOutlinedIcon />}
                                title="Nenhum paciente aguardando"
                                description="Quem entrar na fila de espera aparecerá aqui."
                            />
                        ) : (
                            <List disablePadding>
                                {filaTop.map((f, idx, arr) => (
                                    <React.Fragment key={f.id}>
                                        <ListItem disablePadding sx={{ py: 1.2 }}>
                                            <Avatar sx={{ bgcolor: '#f0fdf9', color: PRIMARY, mr: 1.5, width: 36, height: 36, fontWeight: 700, flexShrink: 0 }}>
                                                {(f.nomePaciente || '?').charAt(0).toUpperCase()}
                                            </Avatar>
                                            <ListItemText sx={{ overflow: 'hidden', minWidth: 0 }}
                                                primary={<Typography variant="body2" fontWeight={600} noWrap>{f.nomePaciente || '-'}</Typography>}
                                                secondary={<Typography variant="caption" color="text.secondary" noWrap>Desde: {fmtData(f.dataEntrada)}</Typography>}
                                            />
                                            <Box sx={{ textAlign: 'right', ml: 1, flexShrink: 0 }}>
                                                {f.prioridade && (
                                                    <Chip label={f.prioridade} size="small" sx={{
                                                        fontWeight: 600,
                                                        bgcolor: f.prioridade === 'ALTA' ? '#fef2f2' : f.prioridade === 'MEDIA' ? '#fffbeb' : '#f0fdf9',
                                                        color: f.prioridade === 'ALTA' ? '#ef4444' : f.prioridade === 'MEDIA' ? '#f59e0b' : PRIMARY,
                                                    }} />
                                                )}
                                            </Box>
                                        </ListItem>
                                        {idx < arr.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
